import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_BASE_URL } from '@app/core/models/constants/app.constants';
import { FdxApiResponse } from '@app/core/responses/fdx-api.response';
import { DataGovernanceQueryDataModel, DataGovernanceQueryPostModel } from '@app/data-governance/models/data-governance-query.model';
import { Observable } from 'rxjs';

// Requests
import { GetExportsRequest } from '@app/exports/services/requests/get-exports.request';
import { RunParallelExportRequest } from '@app/exports/services/requests/run-parallel-export.request';
import { UpdateExportSettingsRequest } from '@app/exports/services/requests/update-export-settings.request';

// Responses
import { ExportInterface } from '@app/exports/models/interfaces/export.interface';
import { CategoryAttributesResponse } from '@app/exports/services/responses/category-attributes.response';
import { CategoryFieldResponse } from '@app/exports/services/responses/category-field.response';
import { DiscoverFieldsResponse } from '@app/exports/services/responses/discover-fields.response';
import { GetExportSettingsResponse } from '@app/exports/services/responses/get-export-settings.response';
import { GetExportsResponse } from '@app/exports/services/responses/get-exports.response';
import { GetWebhookClientEligibilityResponse } from '@app/exports/services/responses/get-webhook-client-eligibility.response';
import { MissingFieldsCategoryPathsResponse } from '@app/exports/services/responses/missing-fields-category-paths.response';
import { MissingFieldsDraftContext, MissingFieldsGateResponse } from '@app/exports/services/responses/missing-fields-gate.response';
import { RunParallelExportResponse } from '@app/exports/services/responses/run-parallel-export.response';
import { TemplateExportMappingResponse } from '@app/exports/services/responses/template-export-mapping.response';
import { UpdateExportSettingsResponse } from '@app/exports/services/responses/update-export-settings.response';
import { ReadinessCheckType } from '@app/exports/utils/readiness-check.util';


@Injectable({
    providedIn: 'root'
})
export class ExportsDataService {

    private readonly baseUrl: string = `${API_BASE_URL}/dbs`;

    constructor(
        private readonly http: HttpClient
    ) { }

    getExports(databaseId: string, requestParams: GetExportsRequest = {}): Observable<GetExportsResponse> {
        let params = new HttpParams();
        if (requestParams.destination_metadata) {
            params = params.append('destination_metadata', requestParams.destination_metadata);
        }

        if (requestParams.include_deactivated_exports) {
            params = params.append('include_deactivated_exports', requestParams.include_deactivated_exports);
        }

        if (requestParams.include_taxonomy_service) {
            params = params.append('include_taxonomy_service', requestParams.include_taxonomy_service);
        }
        return this.http.get<GetExportsResponse>(`/api.php/dbs/${databaseId}/exports`, {params: params});
    }

    addDataGovernanceQuery(databaseId: string, exportId: string, dataGovernanceQuery: DataGovernanceQueryPostModel): Observable<DataGovernanceQueryDataModel> {
        return this.http.post<DataGovernanceQueryDataModel>(`${this.baseUrl}/${databaseId}/exports/${exportId}/data_governance_queries`, dataGovernanceQuery);
    }

    deleteDataGovernanceQuery(databaseId: string, exportId: string, dataGovernanceQueryId: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${databaseId}/exports/${exportId}/data_governance_queries/${dataGovernanceQueryId}`);
    }

    getDataGovernanceQueries(databaseId: string, exportId: string): Observable<DataGovernanceQueryDataModel[]> {
        return this.http.get<DataGovernanceQueryDataModel[]>(`${this.baseUrl}/${databaseId}/exports/${exportId}/data_governance_queries`)
    }

    updateDataGovernanceQuery(databaseId: string, exportId: string, dataGovernanceQuery: DataGovernanceQueryDataModel): Observable<DataGovernanceQueryDataModel> {
        return this.http.put<DataGovernanceQueryDataModel>(`${this.baseUrl}/${databaseId}/exports/${exportId}/data_governance_queries/${dataGovernanceQuery.id}`, dataGovernanceQuery)
    }

    getDesignatedHosts(databaseId: string, url: string): Observable<FdxApiResponse<string[]>> {
        return this.http.post<FdxApiResponse<string[]>>(`/api.php/dbs/${databaseId}/exports/designated_hosts`, {
            url
        });
    }

    addExport(databaseId: string, newExport: any): Observable<ExportInterface> {
        return this.http.post<ExportInterface>(`/api.php/dbs/${databaseId}/exports`, newExport);
    }

    runParallelExport(databaseId: string, exportId: string, params: RunParallelExportRequest): Observable<RunParallelExportResponse> {
        return this.http.post<RunParallelExportResponse>(`/api.php/dbs/${databaseId}/exports/${exportId}/run_parallel_export`, params );
    }

    deleteExport(databaseId: string, exportId: string): Observable<void> {
        return this.http.delete<void>(`/api.php/dbs/${databaseId}/exports/${exportId}`);
    }

    resetExport(databaseId: string, exportId: string): Observable<{ status: string; message?: string; }> {
        return this.http.put<{ status: string; message?: string; }>(`/api.php/dbs/${databaseId}/exports/${exportId}/reset_running`, {});
    }

    updateExport(databaseId: string, exportId: string, newExport: any): Observable<ExportInterface> {
        return this.http.put<ExportInterface>(`/api.php/dbs/${databaseId}/exports/${exportId}`, newExport);
    }

    setFeedUrlConnected(databaseId: string, exportId: string): Observable<{ status: string; }> {
        return this.http.put<{ status: string; }>(`/api.php/dbs/${databaseId}/exports/${exportId}/feed_url_connected`, {});
    }

    getExportSettings(databaseId: string, exportId: string): Observable<GetExportSettingsResponse> {
        return this.http.get<GetExportSettingsResponse>(`${this.baseUrl}/${databaseId}/exports/${exportId}/export_settings`);
    }

    updateExportSettings(databaseId: string, exportId: string, params: UpdateExportSettingsRequest): Observable<UpdateExportSettingsResponse> {
        return this.http.put<UpdateExportSettingsResponse>(`${this.baseUrl}/${databaseId}/exports/${exportId}/export_settings`, params);
    }

    getWebhookClientEligibility(databaseId: string, webhookClient: string): Observable<GetWebhookClientEligibilityResponse> {
        const params = new HttpParams().set('webhook_client', webhookClient);
        return this.http.get<GetWebhookClientEligibilityResponse>(
            `${this.baseUrl}/${databaseId}/exports/webhook_client_details`,
            { params }
        );
    }

    addFieldsFromTaxonomy(databaseId: string, exportId: string, fieldTypes: string[], autoAdd: boolean = true): Observable<DiscoverFieldsResponse> {
        return this.http.post<DiscoverFieldsResponse>(`${this.baseUrl}/${databaseId}/exports/${exportId}/discover-fields`, {
            field_types: fieldTypes,
            auto_add: autoAdd
        });
    }

    /**
     * What the "Add missing fields" button should do for a saved export (FP-16828).
     *
     * Always resolves with a `gate`; failures are reported as `unsupported` + a reason rather than an
     * HTTP error, so the button can always explain itself.
     */
    getMissingFieldsGate(databaseId: string, exportId: string): Observable<MissingFieldsGateResponse> {
        return this.http.get<MissingFieldsGateResponse>(`${this.baseUrl}/${databaseId}/exports/${exportId}/missing_fields/gate`);
    }

    /**
     * Draft twin of {@link getMissingFieldsGate} for the Add Export page, which has no saved export
     * yet and so supplies the in-progress form state instead.
     */
    getMissingFieldsGateForDraft(databaseId: string, context: MissingFieldsDraftContext): Observable<MissingFieldsGateResponse> {
        return this.http.post<MissingFieldsGateResponse>(`${this.baseUrl}/${databaseId}/missing_fields/gate`, context);
    }

    /**
     * Draft twin of {@link getAttributesByCategories} for the Add Export page (FP-16828).
     * TaaS-only: legacy resolution needs a saved export to resolve a marketplace from.
     */
    getMissingFieldsAttributesForDraft(
        databaseId: string,
        context: MissingFieldsDraftContext,
        categories: string[],
        conditions: string[],
        includeAllCategories: boolean = false
    ): Observable<CategoryAttributesResponse> {
        return this.http.post<CategoryAttributesResponse>(`${this.baseUrl}/${databaseId}/missing_fields/attributes`, {
            ...context,
            categories,
            conditions,
            include_all_categories: includeAllCategories
        });
    }

    /**
     * Taxonomy path for each category value, for the Conditions & categories dropdown.
     * Matches feed values against the taxonomy's path values, so ids such as eBay's resolve too.
     */
    getMissingFieldsCategoryPathsForDraft(
        databaseId: string,
        context: MissingFieldsDraftContext,
        categories: string[]
    ): Observable<MissingFieldsCategoryPathsResponse> {
        return this.http.post<MissingFieldsCategoryPathsResponse>(`${this.baseUrl}/${databaseId}/missing_fields/category_paths`, {
            ...context,
            categories
        });
    }

    /**
     * Resolve which export field holds the category value for this export (FP-15642).
     * Drives the "Conditions & categories" dropdown, which runs column stats on the resolved field.
     */
    resolveCategoryField(databaseId: string, exportId: string): Observable<CategoryFieldResponse> {
        return this.http.get<CategoryFieldResponse>(`${this.baseUrl}/${databaseId}/exports/${exportId}/category_field`);
    }

    /**
     * Fetch attributes grouped by category for the chosen categories + conditions (FP-15642),
     * used to render the grid's "Condition & category" column.
     */
    getCategoryAttributes(
        databaseId: string,
        exportId: string,
        categories: string[],
        conditions: string[] = [],
        includeAllCategories: boolean = false
    ): Observable<CategoryAttributesResponse> {
        return this.http.post<CategoryAttributesResponse>(
            `${this.baseUrl}/${databaseId}/exports/${exportId}/category_attributes`,
            {
                categories,
                conditions,
                include_all_categories: includeAllCategories
            }
        );
    }

    /**
     * Fetches the export field mapping for a template, including any Feed AI category field
     * mappings appended for classifiers configured on this db.
     */
    getTemplateExportMapping(databaseId: string, template: string): Observable<TemplateExportMappingResponse> {
        const params = new HttpParams().set('template', template);
        return this.http.get<TemplateExportMappingResponse>(`${this.baseUrl}/${databaseId}/template_export_mapping`, { params });
    }

    /**
     * Runs the LIA/RAAP readiness check and returns a presigned CSV download URL.
     * The endpoint returns `text/plain`, so we request `responseType: 'text'`.
     */
    runReadinessCheck(databaseId: string, type: ReadinessCheckType): Observable<string> {
        const params = new HttpParams()
            .set('type', type)
            .set('download', 'true');
        return this.http.get(`${this.baseUrl}/${databaseId}/lia_raap_report`, {
            params,
            responseType: 'text'
        });
    }

}
